import React, { useState, useEffect, useContext, useRef } from "react"
import { AuthContext } from "../../utils/providers/auth"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./manager-analytics.scss"
import {
	buildCanonicalConversionDataset,
	buildNewRepeatBreakdown,
	getBookingReferenceSeconds,
} from "../../utils/analytics/conversion-metrics"

// ChartJS imports
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title as ChartJSTitle } from "chart.js"
import annotationPlugin from "chartjs-plugin-annotation"

// UI components
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Datepicker from "../../components/ui/datepicker/datepicker"
import Table from "../../components/structure/table/table"
import { CheckIcon, XIcon } from "lucide-react"

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, ChartJSTitle, Tooltip, Legend, annotationPlugin)

const CLIENT_HISTORY_QUERY_CHUNK_SIZE = 10
const DEBOUNCE_FETCH_MS = 500

const getDefaultStats = () => ({
	totalEnquiries: 0,
	totalOriginalEnquiries: 0,
	convertedEnquiries: 0,
	deletedEnquiries: 0,
	conversionRate: 0,
	enquiriesByAgent: {},
	bookingsByAgent: {},
	deletedByAgent: {},
	originalEnquiriesByAgent: {},
	marginsByAgent: {},
	targetsByAgent: {},
	agentNames: {},
	noAgents: false,
	teamTotalMargin: 0,
	teamTotalBookings: 0,
	teamTotalSales: 0,
	newEnquiriesByAgent: {},
	repeatEnquiriesByAgent: {},
	newBookingsByAgent: {},
	repeatBookingsByAgent: {},
	newConversionByAgent: {},
	repeatConversionByAgent: {},
	teamNewEnquiries: 0,
	teamRepeatEnquiries: 0,
	teamNewBookings: 0,
	teamRepeatBookings: 0,
	teamNewConversionRate: 0,
	teamRepeatConversionRate: 0,
})

const chunkArray = (array, chunkSize) => {
	const chunks = []
	for (let i = 0; i < array.length; i += chunkSize) {
		chunks.push(array.slice(i, i + chunkSize))
	}
	return chunks
}

const getTargetWindowForContext = ({ yearMonthMode, selectedYear, selectedMonth, timeframe }) => {
	if (yearMonthMode) {
		return {
			start: moment().year(selectedYear).month(selectedMonth).startOf("month"),
			end: moment().year(selectedYear).month(selectedMonth).endOf("month"),
		}
	}

	if (timeframe === "month") {
		return {
			start: moment().subtract(1, "month").startOf("month"),
			end: moment().subtract(1, "month").endOf("month"),
		}
	}

	if (timeframe === "week") {
		return {
			start: moment().startOf("month"),
			end: moment().endOf("month"),
		}
	}

	return null
}

// Functional component to return the manager analytics dashboard
export default function ManagerAnalytics() {
	const [loading, setLoading] = useState(true)
	const [managers, setManagers] = useState({})
	const [selectedManager, setSelectedManager] = useState("")
	const [timeframe, setTimeframe] = useState("year_month")
	const [customRange, setCustomRange] = useState(false)
	const [yearMonthMode, setYearMonthMode] = useState(true)
	const [selectedYear, setSelectedYear] = useState(moment().year())
	const [selectedMonth, setSelectedMonth] = useState(moment().month())
	const [startDate, setStartDate] = useState(moment().startOf("month").toDate())
	const [endDate, setEndDate] = useState(moment().endOf("month").toDate())
	const [stats, setStats] = useState(getDefaultStats())
	const latestFetchIdRef = useRef(0)

	// Get the current user auth from the context
	const { user } = useContext(AuthContext)

	// On component load
	useEffect(() => {
		// Only admins and managers can access this page
		if (!user.is_admin && !user.is_manager) {
			window.location.href = "/"
			return
		}

		// Fetch all managers
		fetchManagers()
	}, [user])

	// Debounced auto-refresh to avoid repeated heavy fetches during filter changes.
	useEffect(() => {
		if (!selectedManager) return undefined

		const timer = setTimeout(() => {
			fetchAnalytics()
		}, DEBOUNCE_FETCH_MS)

		return () => clearTimeout(timer)
	}, [selectedManager, timeframe, customRange, startDate, endDate, yearMonthMode, selectedYear, selectedMonth])

	const buildClientHistoryMapForClients = async (clientIds) => {
		const uniqueClientIds = [...new Set((clientIds || []).filter(Boolean))]
		if (!uniqueClientIds.length) return new Map()

		const historyMap = new Map()
		const clientChunks = chunkArray(uniqueClientIds, CLIENT_HISTORY_QUERY_CHUNK_SIZE)
		const snapshots = await Promise.all(clientChunks.map((chunk) => db.collection("bookings").where("client", "in", chunk).get()))

		snapshots.forEach((snapshot) => {
			snapshot.docs.forEach((doc) => {
				const data = doc.data()
				const clientId = data.client
				const createdSeconds = getBookingReferenceSeconds(data)
				if (!clientId || !createdSeconds) return

				if (!historyMap.has(clientId) || historyMap.get(clientId) > createdSeconds) {
					historyMap.set(clientId, createdSeconds)
				}
			})
		})

		return historyMap
	}

	// Fetch all managers from the database
	const fetchManagers = async () => {
		try {
			const sortedManagers = {}

			// Get all users who are managers
			const managerDocs = await db.collection("users").where("is_manager", "==", true).get()

			// Collect all managers with their names
			const managersArray = []
			managerDocs.forEach((doc) => {
				const { first_name, last_name } = doc.data()
				managersArray.push({
					id: doc.id,
					name: `${first_name} ${last_name}`,
				})
			})

			// If current user is admin, also include them if they're not already in the list
			if (user.is_admin) {
				const isAlreadyIncluded = managersArray.some((manager) => manager.id === user.uid)
				if (!isAlreadyIncluded) {
					managersArray.push({
						id: user.uid,
						name: `${user.first_name} ${user.last_name} (You)`,
					})
				}
			}

			// Sort managers by name
			managersArray.sort((a, b) => a.name.localeCompare(b.name))

			// Convert back to object format
			managersArray.forEach((manager) => {
				sortedManagers[manager.id] = manager.name
			})

			setManagers(sortedManagers)
			setLoading(false)
		} catch (error) {
			console.error("Error fetching managers:", error)
			setLoading(false)
		}
	}

	// Handle timeframe change
	const handleTimeframeChange = (option) => {
		const selectedOption = option.option
		setTimeframe(selectedOption)
		setCustomRange(selectedOption === "custom")
		setYearMonthMode(selectedOption === "year_month")

		// Update date range based on selected timeframe
		const now = moment()
		if (selectedOption === "week") {
			setStartDate(now.clone().subtract(1, "week").startOf("day").toDate())
			setEndDate(now.endOf("day").toDate())
		} else if (selectedOption === "month") {
			setStartDate(moment().subtract(1, "month").startOf("month").toDate())
			setEndDate(moment().subtract(1, "month").endOf("month").toDate())
		} else if (selectedOption === "year") {
			setStartDate(moment().subtract(1, "year").startOf("year").toDate())
			setEndDate(moment().subtract(1, "year").endOf("year").toDate())
		} else if (selectedOption === "year_month") {
			// Set to current year/month by default
			setSelectedYear(now.year())
			setSelectedMonth(now.month())
			setStartDate(moment().year(now.year()).month(now.month()).startOf("month").toDate())
			setEndDate(moment().year(now.year()).month(now.month()).endOf("month").toDate())
		}
	}

	// Handle year change for year/month mode
	const handleYearChange = (option) => {
		const year = parseInt(option.option)
		setSelectedYear(year)
		setStartDate(moment().year(year).month(selectedMonth).startOf("month").toDate())
		setEndDate(moment().year(year).month(selectedMonth).endOf("month").toDate())
	}

	// Handle month change for year/month mode
	const handleMonthChange = (option) => {
		const month = parseInt(option.option)
		setSelectedMonth(month)
		setStartDate(moment().year(selectedYear).month(month).startOf("month").toDate())
		setEndDate(moment().year(selectedYear).month(month).endOf("month").toDate())
	}

	// Fetch analytics data for the selected manager
	const fetchAnalytics = async () => {
		const fetchId = latestFetchIdRef.current + 1
		latestFetchIdRef.current = fetchId
		setLoading(true)

		try {
			// Calculate date range based on timeframe or custom dates
			let calculatedStartDate, calculatedEndDate
			let shouldCalculateTarget = false
			let targetMonth = ""

			if (customRange) {
				calculatedStartDate = moment(startDate).startOf("day")
				calculatedEndDate = moment(endDate).endOf("day")
			} else if (yearMonthMode) {
				calculatedStartDate = moment().year(selectedYear).month(selectedMonth).startOf("month")
				calculatedEndDate = moment().year(selectedYear).month(selectedMonth).endOf("month")
				shouldCalculateTarget = true
				targetMonth = moment().year(selectedYear).month(selectedMonth).format("YYYY_MM")
			} else {
				const now = moment()
				if (timeframe === "week") {
					calculatedStartDate = now.clone().subtract(1, "week").startOf("day")
					calculatedEndDate = now.endOf("day")
					shouldCalculateTarget = true
					targetMonth = now.format("YYYY_MM")
				} else if (timeframe === "month") {
					calculatedStartDate = now.clone().subtract(1, "month").startOf("month")
					calculatedEndDate = now.clone().subtract(1, "month").endOf("month")
					shouldCalculateTarget = true
					targetMonth = now.subtract(1, "month").format("YYYY_MM")
				} else if (timeframe === "year") {
					calculatedStartDate = now.clone().subtract(1, "year").startOf("year")
					calculatedEndDate = now.clone().subtract(1, "year").endOf("year")
				}
			}

			// Convert to Firebase timestamps
			const startTimestamp = firebase.firestore.Timestamp.fromMillis(calculatedStartDate.valueOf())
			const endTimestamp = firebase.firestore.Timestamp.fromMillis(calculatedEndDate.valueOf())

			// Team scope is intentionally aligned to target-enabled users only.
			const [teamDocsSnapshot, selectedManagerDoc] = await Promise.all([
				db.collection("users").where("manager", "==", selectedManager).get(),
				db.doc(`users/${selectedManager}`).get(),
			])

			const teamUsers = []

			teamDocsSnapshot.forEach((doc) => {
				const data = doc.data()
				if (data.show_for?.targets !== true) return

				teamUsers.push({
					id: doc.id,
					name: `${data.first_name} ${data.last_name}`,
				})
			})

			if (selectedManagerDoc.exists) {
				const managerData = selectedManagerDoc.data()
				if (managerData.show_for?.targets === true) {
					teamUsers.push({
						id: selectedManager,
						name: managers[selectedManager] || `${managerData.first_name || ""} ${managerData.last_name || ""}`.trim() || selectedManager,
					})
				}
			}

			const uniqueTeamUsersMap = new Map()
			teamUsers.forEach((teamUser) => {
				uniqueTeamUsersMap.set(teamUser.id, teamUser)
			})
			const uniqueTeamUsers = Array.from(uniqueTeamUsersMap.values())

			const agentIds = uniqueTeamUsers.map((teamUser) => teamUser.id)
			const agentIdSet = new Set(agentIds)
			const agentNames = uniqueTeamUsers.reduce((acc, teamUser) => {
				acc[teamUser.id] = teamUser.name
				return acc
			}, {})

			if (!agentIds.length) {
				if (fetchId !== latestFetchIdRef.current) return
				setStats({
					...getDefaultStats(),
					noAgents: true,
				})
				setLoading(false)
				return
			}

			// Initialize stats for each agent
			const enquiriesByAgent = {}
			const bookingsByAgent = {}
			const deletedByAgent = {}
			const originalEnquiriesByAgent = {}
			const marginsByAgent = {}
			const targetsByAgent = {}
			const newEnquiriesByAgent = {}
			const repeatEnquiriesByAgent = {}
			const newBookingsByAgent = {}
			const repeatBookingsByAgent = {}
			const newConversionByAgent = {}
			const repeatConversionByAgent = {}

			agentIds.forEach((id) => {
				enquiriesByAgent[id] = 0
				bookingsByAgent[id] = 0
				deletedByAgent[id] = 0
				originalEnquiriesByAgent[id] = 0
				marginsByAgent[id] = {
					totalMargin: 0,
					count: 0,
					average: 0,
				}
				targetsByAgent[id] = {
					monthly: 0,
					achieved: false,
					percentage: 0,
					totalProfit: 0,
				}
				newEnquiriesByAgent[id] = 0
				repeatEnquiriesByAgent[id] = 0
				newBookingsByAgent[id] = 0
				repeatBookingsByAgent[id] = 0
				newConversionByAgent[id] = "N/A"
				repeatConversionByAgent[id] = "N/A"
			})

			const currentMonth = moment().format("YYYY_MM")
			const targetMonthToUse = yearMonthMode ? targetMonth : timeframe === "month" ? moment().subtract(1, "month").format("YYYY_MM") : currentMonth
			const targetFetchPromise = shouldCalculateTarget
				? Promise.all(
						agentIds.map(async (agentId) => {
							const archivedTarget = await db.doc(`targets/${agentId}/archives/${targetMonthToUse}`).get()
							if (archivedTarget.exists) {
								targetsByAgent[agentId].monthly = archivedTarget.data().monthly || 0
								return
							}

							const currentTarget = await db.doc(`targets/${agentId}`).get()
							if (currentTarget.exists) {
								targetsByAgent[agentId].monthly = currentTarget.data().monthly || 0
							}
						})
				  )
				: Promise.resolve()

			const [openEnquiriesSnapshot, closedEnquiriesSnapshot, enquiryBookingsSnapshot, bookingsCreatedInRangeSnapshot] = await Promise.all([
				db.collection("enquiries").where("created", ">=", startTimestamp).where("created", "<=", endTimestamp).get(),
				db.collection("enquiries_removed").where("enquiry.created", ">=", startTimestamp).where("enquiry.created", "<=", endTimestamp).get(),
				db.collection("bookings").where("enquiry.created", ">=", startTimestamp).where("enquiry.created", "<=", endTimestamp).get(),
				db.collection("bookings").where("created", ">=", startTimestamp).where("created", "<=", endTimestamp).get(),
			])

			await targetFetchPromise

			// Keep Team Analytics on the same conversion logic as Conversions and Target Tracker.
			// Build globally (no team filter) so de-duplication matches Conversions exactly.
			const conversionDataset = buildCanonicalConversionDataset({
				openEnquiryDocs: openEnquiriesSnapshot.docs,
				removedEnquiryDocs: closedEnquiriesSnapshot.docs,
				enquiryBookingDocs: enquiryBookingsSnapshot.docs,
				directBookingDocs: bookingsCreatedInRangeSnapshot.docs,
			})

			const { allEnquiries, removedEnquiries, finalEnquiries, allBookings } = conversionDataset
			const teamFinalEnquiries = finalEnquiries.filter((enquiry) => agentIdSet.has(enquiry.agent))
			const teamBookings = allBookings.filter((booking) => agentIdSet.has(booking.agent))

			allEnquiries.forEach((enquiry) => {
				if (originalEnquiriesByAgent[enquiry.agent] != null) {
					originalEnquiriesByAgent[enquiry.agent]++
				}
			})

			removedEnquiries.forEach((enquiry) => {
				if (deletedByAgent[enquiry.agent] != null) {
					deletedByAgent[enquiry.agent]++
				}
			})

			teamFinalEnquiries.forEach((enquiry) => {
				if (enquiriesByAgent[enquiry.agent] != null) {
					enquiriesByAgent[enquiry.agent]++
				}
			})

			let teamTotalMargin = 0
			let teamTotalBookings = 0
			let teamTotalSales = 0

			teamBookings.forEach((booking) => {
				bookingsByAgent[booking.agent]++

				const profit = booking.margins?.profit
				const customerPrice = booking.margins?.customer_price
				if (profit == null || customerPrice == null || customerPrice === 0) return

				const marginPercentage = (profit / customerPrice) * 100
				marginsByAgent[booking.agent].totalMargin += marginPercentage
				marginsByAgent[booking.agent].count++

				teamTotalMargin += profit
				teamTotalBookings++
				teamTotalSales += customerPrice
			})

			agentIds.forEach((agentId) => {
				if (marginsByAgent[agentId].count > 0) {
					marginsByAgent[agentId].average = marginsByAgent[agentId].totalMargin / marginsByAgent[agentId].count
				}
			})

			let teamNewEnquiries = 0
			let teamRepeatEnquiries = 0
			let teamNewBookings = 0
			let teamRepeatBookings = 0

			const relevantClientIds = [...teamFinalEnquiries.map((enquiry) => enquiry.client), ...teamBookings.map((booking) => booking.client)]
			const historyMap = await buildClientHistoryMapForClients(relevantClientIds)

			agentIds.forEach((agentId) => {
				const newRepeatData = buildNewRepeatBreakdown({
					groupId: agentId,
					groupKey: "agent",
					enquiries: teamFinalEnquiries,
					bookings: teamBookings,
					historyMap,
				})

				newEnquiriesByAgent[agentId] = newRepeatData.newEnquiriesCount
				repeatEnquiriesByAgent[agentId] = newRepeatData.repeatEnquiriesCount
				newBookingsByAgent[agentId] = newRepeatData.newBookingsCount
				repeatBookingsByAgent[agentId] = newRepeatData.repeatBookingsCount
				newConversionByAgent[agentId] = newRepeatData.newConversion
				repeatConversionByAgent[agentId] = newRepeatData.repeatConversion

				// Add to team totals
				teamNewEnquiries += newRepeatData.newEnquiriesCount
				teamRepeatEnquiries += newRepeatData.repeatEnquiriesCount
				teamNewBookings += newRepeatData.newBookingsCount
				teamRepeatBookings += newRepeatData.repeatBookingsCount
			})

			const teamNewConversionRate = teamNewEnquiries > 0 ? ((teamNewBookings / teamNewEnquiries) * 100).toFixed(2) : "N/A"
			const teamRepeatConversionRate = teamRepeatEnquiries > 0 ? ((teamRepeatBookings / teamRepeatEnquiries) * 100).toFixed(2) : "N/A"

			const totalOriginalEnquiries = Object.values(originalEnquiriesByAgent).reduce((acc, count) => acc + count, 0)
			const totalEnquiries = Object.values(enquiriesByAgent).reduce((acc, count) => acc + count, 0)
			const totalConverted = Object.values(bookingsByAgent).reduce((acc, count) => acc + count, 0)
			const totalDeleted = Object.values(deletedByAgent).reduce((acc, count) => acc + count, 0)

			if (shouldCalculateTarget) {
				const targetWindow = getTargetWindowForContext({
					yearMonthMode,
					selectedYear,
					selectedMonth,
					timeframe,
				})

				if (targetWindow) {
					const targetStartTimestamp = firebase.firestore.Timestamp.fromMillis(targetWindow.start.valueOf())
					const targetEndTimestamp = firebase.firestore.Timestamp.fromMillis(targetWindow.end.valueOf())
					const targetBookingsSnapshot = await db.collection("bookings").where("created", ">=", targetStartTimestamp).where("created", "<=", targetEndTimestamp).get()

					const targetProfitByAgent = {}
					targetBookingsSnapshot.docs.forEach((doc) => {
						const bookingData = doc.data()
						if (!agentIdSet.has(bookingData.agent)) return
						if (bookingData.margins?.profit == null) return

						targetProfitByAgent[bookingData.agent] = (targetProfitByAgent[bookingData.agent] || 0) + bookingData.margins.profit
					})

					agentIds.forEach((agentId) => {
						if (targetsByAgent[agentId].monthly <= 0) return

						const targetTotalProfit = targetProfitByAgent[agentId] || 0
						targetsByAgent[agentId].totalProfit = targetTotalProfit

						const percentage = (targetTotalProfit / targetsByAgent[agentId].monthly) * 100
						targetsByAgent[agentId].percentage = percentage.toFixed(1)
						targetsByAgent[agentId].achieved = targetTotalProfit >= targetsByAgent[agentId].monthly
					})
				}
			}

			const conversionRate = totalEnquiries > 0 ? (totalConverted / totalEnquiries) * 100 : 0

			if (fetchId !== latestFetchIdRef.current) return
			setStats({
				totalEnquiries,
				totalOriginalEnquiries,
				convertedEnquiries: totalConverted,
				deletedEnquiries: totalDeleted,
				conversionRate: conversionRate.toFixed(2),
				enquiriesByAgent,
				originalEnquiriesByAgent,
				bookingsByAgent,
				deletedByAgent,
				marginsByAgent,
				targetsByAgent,
				agentNames,
				teamTotalMargin,
				teamTotalBookings,
				teamTotalSales,
				newEnquiriesByAgent,
				repeatEnquiriesByAgent,
				newBookingsByAgent,
				repeatBookingsByAgent,
				newConversionByAgent,
				repeatConversionByAgent,
				teamNewEnquiries,
				teamRepeatEnquiries,
				teamNewBookings,
				teamRepeatBookings,
				teamNewConversionRate,
				teamRepeatConversionRate,
			})

			setLoading(false)
		} catch (error) {
			console.error("Error fetching analytics:", error)
			if (fetchId !== latestFetchIdRef.current) return
			setLoading(false)
		}
	}

	// Prepare data for the conversion rate pie chart
	const conversionPieData = {
		labels: ["Converted", "Deleted", "Open/In Progress"],
		datasets: [
			{
				data: [stats.convertedEnquiries, stats.deletedEnquiries, stats.totalEnquiries - stats.convertedEnquiries - stats.deletedEnquiries],
				backgroundColor: ["rgba(75, 192, 192, 0.6)", "rgba(255, 99, 132, 0.6)", "rgba(54, 162, 235, 0.6)"],
				borderColor: ["rgba(75, 192, 192, 1)", "rgba(255, 99, 132, 1)", "rgba(54, 162, 235, 1)"],
				borderWidth: 1,
			},
		],
	}

	// Prepare data for the agent performance bar chart
	const agentPerformanceData = {
		labels: stats.agentNames ? Object.keys(stats.enquiriesByAgent).map((id) => stats.agentNames[id]) : [],
		datasets: [
			{
				label: "Total Enquiries",
				data: Object.values(stats.enquiriesByAgent),
				backgroundColor: "rgba(54, 162, 235, 0.6)",
				borderColor: "rgba(54, 162, 235, 1)",
				borderWidth: 1,
			},
			{
				label: "Converted to Bookings",
				data: Object.values(stats.bookingsByAgent),
				backgroundColor: "rgba(75, 192, 192, 0.6)",
				borderColor: "rgba(75, 192, 192, 1)",
				borderWidth: 1,
			},
			{
				label: "Deleted",
				data: Object.values(stats.deletedByAgent),
				backgroundColor: "rgba(255, 99, 132, 0.6)",
				borderColor: "rgba(255, 99, 132, 1)",
				borderWidth: 1,
			},
		],
	}

	// Options for the bar chart
	const barOptions = {
		responsive: true,
		scales: {
			y: {
				beginAtZero: true,
				title: {
					display: true,
					text: "Number of Enquiries",
				},
			},
			x: {
				title: {
					display: true,
					text: "Agents",
				},
			},
		},
		plugins: {
			legend: {
				position: "top",
			},
			title: {
				display: true,
				text: "Agent Performance",
			},
		},
	}

	// Get timeframe display text
	const getTimeframeText = () => {
		if (customRange) {
			return `${moment(startDate).format("MMM D, YYYY")} - ${moment(endDate).format("MMM D, YYYY")}`
		}

		if (yearMonthMode) {
			return `${moment().month(selectedMonth).format("MMMM")} ${selectedYear}`
		}

		const timeframeOptions = {
			week: "Last Week",
			month: "Last Month",
			year: "Last Year",
			custom: "Custom Range",
			year_month: "Select Month",
		}

		return timeframeOptions[timeframe] || "Select timeframe"
	}

	// Handle date changes
	const handleStartDateChange = (milliseconds) => {
		setStartDate(moment(milliseconds).toDate())
	}

	const handleEndDateChange = (milliseconds) => {
		setEndDate(moment(milliseconds).toDate())
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>HoS Team Analytics</h1>
			</Title>

			<div className="analytics-controls">
				<div className="select-wrapper">
					<label>Select Manager:</label>
					<Select
						placeholder="Choose a manager"
						value={selectedManager ? managers[selectedManager] : ""}
						onSelect={(option) => setSelectedManager(option.option)}
						options={managers}
					/>
				</div>

				<div className="select-wrapper">
					<label>Timeframe:</label>
					<Select
						placeholder="Select timeframe"
						value={getTimeframeText()}
						onSelect={handleTimeframeChange}
						options={{
							year_month: "Select Month",
							week: "Last Week",
							month: "Last Month",
							year: "Last Year",
							custom: "Custom Range",
						}}
					/>
				</div>
			</div>

			{customRange && (
				<div className="date-range-controls">
					<div className="date-picker-wrapper">
						<label>Start Date:</label>
						<Datepicker
							placeholder="Start Date"
							value={moment(startDate).valueOf()}
							onSelect={handleStartDateChange}
						/>
					</div>
					<div className="date-picker-wrapper">
						<label>End Date:</label>
						<Datepicker
							placeholder="End Date"
							value={moment(endDate).valueOf()}
							onSelect={handleEndDateChange}
						/>
					</div>
				</div>
			)}

			{yearMonthMode && (
				<div className="date-range-controls">
					<div className="date-picker-wrapper">
						<label>Year:</label>
						<Select
							placeholder="Select Year"
							value={selectedYear.toString()}
							onSelect={handleYearChange}
							options={Object.fromEntries(
								Array.from({ length: 5 }, (_, i) => {
									const year = moment().year() - i
									return [year.toString(), year.toString()]
								})
							)}
						/>
					</div>
					<div className="date-picker-wrapper">
						<label>Month:</label>
						<Select
							placeholder="Select Month"
							value={moment().month(selectedMonth).format("MMMM")}
							onSelect={handleMonthChange}
							options={Object.fromEntries(Array.from({ length: 12 }, (_, i) => [i.toString(), moment().month(i).format("MMMM")]))}
						/>
					</div>
				</div>
			)}

				{loading ? (
					<div className="loading">Loading analytics data...</div>
				) : selectedManager ? (
					<div className="analytics-content">
						{stats.noAgents ? (
							<div className="no-agents-message">
								<p>No target-enabled users were found for this manager. Team Analytics only includes users enabled for targets.</p>
							</div>
						) : (
							<>
							<div className="stats-summary">
								<div className="stat-card">
									<h3>Total Enquiries</h3>
									<div className="stat-value">{stats.totalOriginalEnquiries}</div>
									<div className="stat-subvalue">({stats.totalEnquiries} de-duped)</div>
								</div>
								<div className="stat-card">
									<h3>Deleted</h3>
									<div className="stat-value">{stats.deletedEnquiries}</div>
								</div>
								<div className="stat-card">
									<h3>Bookings</h3>
									<div className="stat-value">{stats.convertedEnquiries}</div>
								</div>
								<div className="stat-card">
									<h3>Conversion Rate</h3>
									<div className="stat-value">{stats.conversionRate}%</div>
								</div>
								<div className="stat-card">
									<h3>New Business Conv.</h3>
									<div className="stat-value">{stats.teamNewConversionRate === "N/A" ? "N/A" : `${stats.teamNewConversionRate}%`}</div>
									<div className="stat-subvalue">
										({stats.teamNewBookings} / {stats.teamNewEnquiries})
									</div>
								</div>
								<div className="stat-card">
									<h3>Repeat Conv.</h3>
									<div className="stat-value">{stats.teamRepeatConversionRate === "N/A" ? "N/A" : `${stats.teamRepeatConversionRate}%`}</div>
									<div className="stat-subvalue">
										({stats.teamRepeatBookings} / {stats.teamRepeatEnquiries})
									</div>
								</div>
								<div className="stat-card">
									<h3>Team Total Sales</h3>
									<div className="stat-value">£{stats.teamTotalSales.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
									<div className="stat-subvalue">({stats.teamTotalBookings} bookings)</div>
								</div>
								<div className="stat-card">
									<h3>Team Total Margin</h3>
									<div className="stat-value">£{stats.teamTotalMargin.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
									<div className="stat-subvalue">({stats.teamTotalBookings} bookings)</div>
								</div>
							</div>

							{/* <div className="charts-container">
								<div className="chart-wrapper pie-chart">
									<h3>Enquiry Status Distribution</h3>
									<Pie data={conversionPieData} />
								</div>

								<div className="chart-wrapper bar-chart">
									<h3>Agent Performance</h3>
									<Bar
										data={agentPerformanceData}
										options={barOptions}
									/>
								</div>
							</div> */}

								<div className="agent-table-container">
									<h3>Agent Performance Details</h3>
									<Table
										className="enquiries-table conversions-table manager-analytics-table"
										headings={[
											"Agent",
											"Enquiries (De-duped)",
											"Converted",
											"Conv. Rate (Combined)",
											"New Enquiries",
											"New Bookings",
											"Conv. Rate (New)",
											"Repeat Enquiries",
											"Repeat Bookings",
											"Conv. Rate (Repeat)",
											"Avg. Margin %",
											"Target",
										]}>
										{Object.keys(stats.enquiriesByAgent)
											.sort((a, b) => stats.agentNames[a].localeCompare(stats.agentNames[b]))
											.map((agentId) => {
												const totalEnquiries = stats.enquiriesByAgent[agentId]
												const converted = stats.bookingsByAgent[agentId]
												const conversionRate = totalEnquiries > 0 ? ((converted / totalEnquiries) * 100).toFixed(2) : "0.00"
												const avgMargin = stats.marginsByAgent && stats.marginsByAgent[agentId] ? stats.marginsByAgent[agentId].average.toFixed(2) : "0.00"
												const targetData = stats.targetsByAgent[agentId]

											// New/repeat data
											const newEnquiries = stats.newEnquiriesByAgent[agentId] || 0
											const repeatEnquiries = stats.repeatEnquiriesByAgent[agentId] || 0
											const newBookings = stats.newBookingsByAgent[agentId] || 0
											const repeatBookings = stats.repeatBookingsByAgent[agentId] || 0
											const newConversion = stats.newConversionByAgent[agentId] || "N/A"
											const repeatConversion = stats.repeatConversionByAgent[agentId] || "N/A"

												return (
													<tr key={agentId}>
														<td>{stats.agentNames[agentId]}</td>
														<td>{totalEnquiries}</td>
														<td>{converted}</td>
														<td>{conversionRate}%</td>
														<td className="group-separator">{newEnquiries}</td>
														<td>{newBookings}</td>
														<td>{newConversion === "N/A" ? "N/A" : `${newConversion}%`}</td>
														<td className="group-separator">{repeatEnquiries}</td>
														<td>{repeatBookings}</td>
														<td>{repeatConversion === "N/A" ? "N/A" : `${repeatConversion}%`}</td>
														<td className="group-separator">{avgMargin}%</td>
														<td>
															{!timeframe.match(/^(week|month)$/) && !yearMonthMode ? (
																<small>-</small>
															) : targetData.monthly > 0 ? (
															<div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
																{targetData.achieved ? (
																	<CheckIcon
																		size={19}
																		color="#22b229"
																	/>
																) : (
																	<XIcon
																		size={19}
																		color="#d3423d"
																	/>
																)}
																<div>
																	<span className={`ui-badge ${targetData.percentage >= 100 ? "POSITIVE" : "NEGATIVE"}`}>{targetData.percentage}%</span>
																</div>
															</div>
														) : (
															<small>No target</small>
														)}
														</td>
													</tr>
												)
											})}
									</Table>
								</div>
						</>
					)}
				</div>
			) : (
				<div className="no-selection">
					<p>Please select a manager to view their team's analytics.</p>
				</div>
			)}
		</Tile>
	)
}
